import { BackgroundCheckColumns } from '@/backend/features/background-check/shared';
import { useRequestBackgroundCheckDetails } from '@/ui/features/background-check/hooks';
import { SlideOutMenuItemWithId } from '@/ui/hooks';
import { handleErrorDialog, showConfirmDialog } from '@juvare/common-web-ui/DialogHelper';
import { showInfoSnackbar } from '@juvare/common-web-ui/SnackbarHelper';
import { SlideoutMenuItem } from '@juvare/contact-management/ui/components/SlideoutMenuButton';
import { CustomAccountSlideoutMenuItemsParams } from '@juvare/contact-management/ui/features/accounts/components/AccountsPanel/components';
import { requestLicenseCheckFunctionDef } from '@juvare/credentialing/backend/features/credentialing-verification/shared';
import { allowRequestLicenseCheck } from '@juvare/credentialing/backend/features/permissions';
import { useTypedPlatformFunction } from '@juvare/platform-sdk-react/ui/features/typed-platform-functions';
import { getUserState } from '@juvare/platform-sdk-react/ui/stores';
import { Row } from '@juvare/platform-shared/features/storage';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

export const useAccountSlideoutAdditionalMenuItems: (backgroundChecks?: Row<BackgroundCheckColumns>[]) => {
    getAccountAdditionalMenuItems: (props: CustomAccountSlideoutMenuItemsParams) => SlideOutMenuItemWithId[];
} = (backgroundChecks?: Row<BackgroundCheckColumns>[]) => {
    const userState = useSelector(getUserState);
    const { t } = useTranslation();

    const { getBackgroundCheckMenuItems, isAllowedToRequestBackgroundCheck } = useRequestBackgroundCheckDetails();
    const requestLicenseCheck = useTypedPlatformFunction(requestLicenseCheckFunctionDef);

    const getAccountAdditionalMenuItems = React.useCallback(
        (props: CustomAccountSlideoutMenuItemsParams): SlideOutMenuItemWithId[] => {
            const { account, accountReload, accountListReload } = props;

            const menuItems = props.menuItems.map(toMenuItem);
            const reloadAccountAndList = () => {
                accountReload();
                accountListReload?.();
            };

            if (isAllowedToRequestBackgroundCheck(account.data.systemStatus, !!backgroundChecks?.length)) {
                menuItems.splice(0, 0, ...getBackgroundCheckMenuItems(account, backgroundChecks, reloadAccountAndList));
            }

            if (allowRequestLicenseCheck(userState) && account.data.systemStatus === 'active') {
                menuItems.splice(1, 0, {
                    id: 'menu-item-license-check',
                    label: t('menuItems.verifyCredentials'),
                    onClick: () => {
                        showConfirmDialog({
                            title: t('credentialing.titles.verifyCredentials'),
                            onConfirm: () => {
                                requestLicenseCheck({
                                    personId: account.id,
                                    requestedBy: userState?.displayName,
                                })
                                    .then(() => {
                                        showInfoSnackbar({ message: t('credentialing.messages.licenseCheckRequested') });
                                    })
                                    .catch(handleErrorDialog)
                                    .finally(() => {
                                        reloadAccountAndList();
                                    });
                            },
                        });
                    },
                    'data-test': 'menu-item-verify-credentials',
                });
            }

            return menuItems;
        },
        [getBackgroundCheckMenuItems, backgroundChecks, isAllowedToRequestBackgroundCheck, t, userState, requestLicenseCheck],
    );

    return { getAccountAdditionalMenuItems };
};

function toMenuItem(m: SlideoutMenuItem): SlideOutMenuItemWithId {
    return {
        ...m,
        onClick: m.onClick || (() => undefined),
    };
}
